import React, { memo, useMemo } from 'react';
import { View, Text, StyleSheet, Image, Platform } from 'react-native';
import { SvgIcon } from '../../components/common/SvgIcon';
import { Clickable, PRESS_PRESETS } from '../../components/common/Clickable';
import { Input } from '../../components/common/Input';
import { ButtonLoader } from '../../components/load-main/ButtonLoader';
import { ContentCard } from '../../components/common/ContentCard';
import { appTheme, type ThemeColors } from '../../shared/theme';
import { studioCardShell, DASH_STUDIO_RADIUS } from '../../shared/theme/studio/dashboardConstants';
import { androidScaleFont } from '../../shared/theme/studio/stackScreenShell';
import { useRtl } from '../../shared/i18n/useRtl';
import { horizontalLtrDirectionStyle } from '../../shared/i18n/rtl';
import { resolveImageUri } from '../../shared/utils/resolveImageUri';
import type { CommunityPost, CommunityComment } from '../../data/domains/community';

const ANDROID_TEXT = Platform.OS === 'android' ? { includeFontPadding: false } : {};
const AVATAR = 40;
const COMMENT_AVATAR = 28;
const POST_MAX_CHARS = 1000;

type PostCardProps = {
  post: CommunityPost;
  colors: ThemeColors;
  expanded: boolean;
  comment_draft: string;
  is_submitting_comment: boolean;
  onLike: () => void;
  onToggleComments: () => void;
  onCommentDraftChange: (text: string) => void;
  onSubmitComment: () => void;
  labels: {
    user_fallback: string;
    like: string;
    unlike: string;
    add_comment: string;
    add_comment_placeholder: string;
    send_comment: string;
    view_comments: string;
    hide_comments: string;
    comments: string;
  };
};

function avatar_initial(name: string | undefined, fallback: string): string {
  const source = (name || fallback).trim();
  return source ? source.charAt(0).toLocaleUpperCase() : '?';
}

function AuthorAvatar({
  uri,
  name,
  fallback,
  size,
  colors,
  styles,
}: {
  uri?: string;
  name?: string;
  fallback: string;
  size: number;
  colors: ThemeColors;
  styles: ReturnType<typeof createPostCardStyles>;
}) {
  const resolved = resolveImageUri(uri);
  if (resolved) {
    return <Image source={{ uri: resolved }} style={{ width: size, height: size, borderRadius: size / 2 }} />;
  }
  return (
    <View
      style={[
        styles.avatarFallback,
        {
          width: size,
          height: size,
          borderRadius: size / 2,
          backgroundColor: colors.primaryUltra ?? `${colors.primary}22`,
        },
      ]}>
      <Text style={[styles.avatarInitial, { color: colors.primary, fontSize: Math.round(size * 0.38) }, ANDROID_TEXT]}>
        {avatar_initial(name, fallback)}
      </Text>
    </View>
  );
}

export const PostCard = memo(function PostCard({
  post,
  colors,
  expanded,
  comment_draft,
  is_submitting_comment,
  onLike,
  onToggleComments,
  onCommentDraftChange,
  onSubmitComment,
  labels,
}: PostCardProps) {
  const rtl = useRtl();
  const styles = useMemo(() => createPostCardStyles(colors), [colors]);
  const writing = rtl.isRTL ? 'rtl' : 'ltr';
  const text_align = rtl.isRTL ? 'right' : 'left';
  const comments = post.comments ?? [];
  const reaction_count = Math.max(0, Number(post.total_reactions) || 0);
  const comment_count = comments.length;
  const draft = comment_draft;
  const can_send = draft.trim().length > 0 && !is_submitting_comment;
  const attachment_uri = resolveImageUri(post.attachment);
  const author_name = post.customer?.name || labels.user_fallback;

  return (
    <ContentCard
      variant="surface"
      padding="none"
      style={[styles.card, studioCardShell(colors), { borderRadius: DASH_STUDIO_RADIUS, overflow: 'hidden' }]}>
      <View style={[styles.header, horizontalLtrDirectionStyle]}>
        <AuthorAvatar
          uri={post.customer?.picture}
          name={author_name}
          fallback={labels.user_fallback}
          size={AVATAR}
          colors={colors}
          styles={styles}
        />
        <View style={styles.headerText}>
          <Text style={[styles.userName, { color: colors.text, textAlign: text_align, writingDirection: writing }, ANDROID_TEXT]} numberOfLines={1}>
            {author_name}
          </Text>
          <Text style={[styles.timeText, { color: colors.textSecondary, textAlign: text_align, writingDirection: writing }, ANDROID_TEXT]}>
            {post.created_at}
          </Text>
        </View>
      </View>

      {post.content ? (
        <Text style={[styles.caption, { color: colors.text, textAlign: text_align, writingDirection: writing }, ANDROID_TEXT]}>{post.content}</Text>
      ) : null}

      {attachment_uri ? (
        <Image source={{ uri: attachment_uri }} style={styles.postImage} resizeMode="cover" accessibilityIgnoresInvertColors />
      ) : null}

      <View style={[styles.actionRow, horizontalLtrDirectionStyle, { borderTopColor: colors.borderSubtle }]}>
        <Clickable
          onPress={onLike}
          haptics
          pressScale={PRESS_PRESETS.button.scale}
          style={styles.actionBtn}
          accessibilityLabel={post.is_reacted ? labels.unlike : labels.like}
          accessibilityRole="button">
          <View style={[styles.actionBtnInner, horizontalLtrDirectionStyle]}>
            <SvgIcon
              name={post.is_reacted ? 'heart-fill' : 'heart'}
              size={20}
              color={post.is_reacted ? colors.error : colors.textSecondary}
            />
            {reaction_count > 0 ? (
              <Text style={[styles.actionCount, { color: post.is_reacted ? colors.error : colors.textSecondary }, ANDROID_TEXT]}>
                {String(reaction_count)}
              </Text>
            ) : null}
          </View>
        </Clickable>

        <Clickable
          onPress={onToggleComments}
          haptics
          pressScale={PRESS_PRESETS.button.scale}
          style={styles.actionBtn}
          accessibilityLabel={expanded ? labels.hide_comments : labels.view_comments}
          accessibilityRole="button">
          <View style={[styles.actionBtnInner, horizontalLtrDirectionStyle]}>
            <SvgIcon name="chat" size={20} color={expanded ? colors.primary : colors.textSecondary} />
            {comment_count > 0 ? (
              <Text style={[styles.actionCount, { color: expanded ? colors.primary : colors.textSecondary }, ANDROID_TEXT]}>
                {String(comment_count)}
              </Text>
            ) : null}
          </View>
        </Clickable>
      </View>

      {expanded ? (
        <View style={[styles.commentsBlock, { borderTopColor: colors.borderSubtle }]}>
          {comment_count > 0 ? (
            <Text style={[styles.commentsHeading, { color: colors.textSecondary, textAlign: text_align, writingDirection: writing }, ANDROID_TEXT]}>
              {labels.comments}
            </Text>
          ) : null}

          {comments.map((c: CommunityComment) => {
            const comment_name = c.customer?.name || labels.user_fallback;
            return (
              <View key={c.id} style={[styles.commentRow, horizontalLtrDirectionStyle]}>
                <AuthorAvatar
                  uri={c.customer?.picture}
                  name={comment_name}
                  fallback={labels.user_fallback}
                  size={COMMENT_AVATAR}
                  colors={colors}
                  styles={styles}
                />
                <View style={[styles.commentBubble, { backgroundColor: colors.surfaceLight ?? colors.surfaceFade ?? `${colors.text}08` }]}>
                  <Text
                    style={[styles.commentAuthor, { color: colors.text, textAlign: text_align, writingDirection: writing }, ANDROID_TEXT]}
                    numberOfLines={1}>
                    {comment_name}
                  </Text>
                  <Text style={[styles.commentBody, { color: colors.text, textAlign: text_align, writingDirection: writing }, ANDROID_TEXT]}>
                    {c.content}
                  </Text>
                  {c.created_at ? (
                    <Text style={[styles.commentTime, { color: colors.textLight, textAlign: text_align, writingDirection: writing }, ANDROID_TEXT]}>
                      {c.created_at}
                    </Text>
                  ) : null}
                </View>
              </View>
            );
          })}

          <View style={[styles.composerRow, horizontalLtrDirectionStyle]}>
            <Input
              placeholder={labels.add_comment_placeholder}
              value={draft}
              onChangeText={onCommentDraftChange}
              multiline
              maxLength={500}
              containerStyle={styles.composerInput}
              inputStyle={styles.composerInputText}
            />
            <Clickable
              onPress={onSubmitComment}
              disabled={!can_send}
              haptics={can_send}
              pressScale={PRESS_PRESETS.button.scale}
              style={[
                styles.sendBtn,
                {
                  backgroundColor: can_send ? colors.primary : colors.borderLight,
                  opacity: can_send ? 1 : 0.7,
                },
              ]}
              accessibilityLabel={labels.send_comment}
              accessibilityRole="button">
              <View style={styles.sendBtnInner}>
                {is_submitting_comment ? (
                  <ButtonLoader size={18} color={colors.onPrimary} />
                ) : (
                  <SvgIcon name="arrow-forward" size={18} color={can_send ? colors.onPrimary : colors.textSecondary} />
                )}
              </View>
            </Clickable>
          </View>
        </View>
      ) : (
        <Clickable
          onPress={onToggleComments}
          haptics
          pressScale={PRESS_PRESETS.listRow.scale}
          style={[styles.addCommentTrigger, { borderTopColor: colors.borderSubtle }]}
          accessibilityLabel={labels.add_comment}
          accessibilityRole="button">
          <View style={[styles.addCommentTriggerInner, horizontalLtrDirectionStyle]}>
            <SvgIcon name="chat" size={16} color={colors.primary} />
            <Text style={[styles.addCommentTriggerText, { color: colors.primary }, ANDROID_TEXT]}>{labels.add_comment}</Text>
          </View>
        </Clickable>
      )}
    </ContentCard>
  );
});

function createPostCardStyles(c: ThemeColors) {
  const sf = androidScaleFont;
  return StyleSheet.create({
    card: {
      width: '100%',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm + 2,
      paddingHorizontal: appTheme.spacing.md,
      paddingTop: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.sm,
    },
    avatarFallback: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    avatarInitial: {
      fontWeight: '700',
    },
    headerText: {
      flex: 1,
      minWidth: 0,
      gap: 2,
    },
    userName: {
      fontSize: sf(15),
      fontWeight: '700',
      letterSpacing: -0.2,
    },
    timeText: {
      fontSize: sf(12),
      fontWeight: '500',
    },
    caption: {
      fontSize: sf(15),
      lineHeight: sf(22),
      paddingHorizontal: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.sm + 2,
    },
    postImage: {
      width: '100%',
      aspectRatio: 4 / 3,
      backgroundColor: c.borderLight,
    },
    actionRow: {
      flexDirection: 'row',
      alignItems: 'center',
      minHeight: 48,
      paddingHorizontal: appTheme.spacing.sm,
      paddingVertical: 4,
      borderTopWidth: StyleSheet.hairlineWidth,
      gap: 2,
    },
    actionBtn: {
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderRadius: appTheme.borderRadius.md,
    },
    actionBtnInner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    actionCount: {
      fontSize: sf(13),
      fontWeight: '600',
      minWidth: 12,
    },
    commentsBlock: {
      borderTopWidth: StyleSheet.hairlineWidth,
      paddingHorizontal: appTheme.spacing.md,
      paddingTop: appTheme.spacing.sm,
      paddingBottom: appTheme.spacing.md,
      gap: appTheme.spacing.sm,
    },
    commentsHeading: {
      fontSize: sf(12),
      fontWeight: '700',
      letterSpacing: 0.3,
      textTransform: 'uppercase',
      marginBottom: 2,
    },
    commentRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: appTheme.spacing.sm,
    },
    commentBubble: {
      flex: 1,
      minWidth: 0,
      borderRadius: 14,
      paddingHorizontal: appTheme.spacing.sm + 2,
      paddingVertical: appTheme.spacing.sm,
      gap: 2,
    },
    commentAuthor: {
      fontSize: sf(13),
      fontWeight: '700',
    },
    commentBody: {
      fontSize: sf(14),
      lineHeight: sf(20),
    },
    commentTime: {
      fontSize: sf(11),
      marginTop: 2,
      fontWeight: '500',
    },
    composerRow: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      gap: appTheme.spacing.sm,
      marginTop: 4,
    },
    composerInput: {
      flex: 1,
      marginBottom: 0,
    },
    composerInputText: {
      minHeight: 40,
      maxHeight: 96,
      fontSize: sf(14),
      lineHeight: sf(20),
      paddingTop: Platform.OS === 'ios' ? 10 : 8,
      paddingBottom: Platform.OS === 'ios' ? 10 : 8,
    },
    sendBtn: {
      width: 40,
      height: 40,
      borderRadius: 20,
      alignItems: 'center',
      justifyContent: 'center',
    },
    sendBtnInner: {
      alignItems: 'center',
      justifyContent: 'center',
    },
    addCommentTrigger: {
      borderTopWidth: StyleSheet.hairlineWidth,
      paddingVertical: 12,
      paddingHorizontal: appTheme.spacing.md,
    },
    addCommentTriggerInner: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    addCommentTriggerText: {
      fontSize: sf(14),
      fontWeight: '600',
    },
  });
}

export const COMMUNITY_POST_MAX_CHARS = POST_MAX_CHARS;
